import { useEffect, useMemo, useRef, useState } from 'react';
import './showcase.css';

export interface ShowcaseFilm {id:string;title:string;label:string;motion:string;poster?:string;mime?:string}
export interface ShowcaseProps {publicPreview:boolean;heroUrl:string;loadFilms():Promise<ShowcaseFilm[]>;enter(create:boolean):void;open(id:string):void;probeHero():Promise<boolean>}

const HERO_POSTER = '/static/caprica-showreel-poster-v2.webp';
/** The showreel's chapters, in seconds from the start of the hero film. */
export const HERO_CHAPTERS = [
  {start: 0, label: 'Colour · The Impossible Passage', copy: 'A flower market in full sun: Rumi, a bouquet, and a girl in yellow with one more flower.'},
  {start: 5, label: 'Purity · Existence', copy: 'From the first living cell to the first question — one continuous journey through time.'},
  {start: 11.2, label: 'Lyric video · Existence', copy: "The song's own words, timed to the voice and set into the picture."},
  {start: 15.5, label: 'Lip-sync close-up · When Tomorrow Came', copy: 'Rumi sings every line herself, alone on a dark theatre stage.'},
  {start: 19.3, label: 'Human & AI · Existence', copy: 'A scientist and her android look for what makes a mind — under the same stars.'},
  {start: 23.7, label: 'Science fiction · Existence II', copy: 'Longevity, synthetic minds and the Eternal Question.'},
  {start: 28, label: 'Beauty & dance · More Than Real', copy: 'Light shatters and three dancers move as one — in a glass amphitheatre.'},
  {start: 33.6, label: 'Action · NEON DOJO', copy: "A robot dance battle on Tokyo's waterfront, at full speed."},
] as const;

export function heroChapterAt(seconds: number): number {
  let current = 0;
  HERO_CHAPTERS.forEach((chapter, index) => { if (seconds >= chapter.start) current = index; });
  return current;
}
// Gallery rhythm, repeated: large frames, tall frames and wide frames, like a contact sheet.
const TILE_RHYTHM = ['big', '', 'tall', '', '', 'wide', '', 'tall', '', 'wide', '', ''];
const METHOD = [
  ['Listen', 'Bring the song and the spark.', 'Upload the track, lyrics when relevant, and a few words about the film you imagine.'],
  ['Understand', 'Caprica finds what matters.', 'Characters, wardrobe, locations, motifs, performance and motion become a form you can correct.'],
  ['Direct', 'Review the world before the spend.', 'See the storyboard, context and exact credit route. Approve or change only what needs attention.'],
  ['Finish', 'Build one coherent film.', 'Approved images become motion, lip-sync where useful and a final cut with the original song intact.'],
] as const;
const PRINCIPLES = [
  ['Continuity', 'The same people. The same world.', 'Reviewed cast, clothing, locations and camera setups travel only to the scenes where they belong.'],
  ['Clarity', 'Questions appear in context.', 'Scene-level signals replace walls of technical text, with detail available exactly when it matters.'],
  ['Control', 'Know the route before the credits move.', 'Model choices and fallbacks stay visible. Earlier versions remain available while the next cut evolves.'],
] as const;

function pad(value: number) {
  return String(value).padStart(2, '0');
}

/** Plays only while on screen, so a wall of films never competes for the connection. */
function Video({src,poster,reduced,mime}:{src:string;poster?:string;reduced:boolean;mime?:string}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video || reduced) return;
    if (typeof IntersectionObserver === 'undefined') {
      void video.play()?.catch(() => {});
      return () => video.pause();
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) void video.play()?.catch(() => {});
      else video.pause();
    }, {rootMargin: '80px'});
    observer.observe(video);
    return () => {
      observer.disconnect();
      video.pause();
    };
  }, [src, reduced]);
  if (reduced && poster) return <img src={poster} alt="" />;
  return <video ref={ref} muted loop playsInline preload="metadata" poster={poster} aria-hidden="true"><source src={src} type={mime || 'video/mp4'} /></video>;
}

// The showreel opens on the Caprica logo (0–1 s), shows what Caprica makes, and closes on the
// logo again (from 40.7 s). The logo plays once as the page's opening title; after that the reel loops.
const HERO_CUT = {logoEnd: 0.8, storyStart: 1.0, storyEnd: 40.7};

function HeroFilm({src, poster, setIntro, onChapter}: {src: string; poster: string; setIntro(on: boolean): void; onChapter(index: number): void}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let frame = 0;
    let introDone = false;
    let safety = 0;
    const finishIntro = () => {
      if (introDone) return;
      introDone = true;
      window.clearTimeout(safety);
      setIntro(false);
    };
    const follow = () => {
      onChapter(heroChapterAt(video.currentTime));
      if (video.currentTime >= HERO_CUT.logoEnd) finishIntro();
      if (video.currentTime >= HERO_CUT.storyEnd || video.ended) {
        video.currentTime = HERO_CUT.storyStart;
        void video.play()?.catch(() => {});
      }
    };
    const tick = () => {
      follow();
      frame = window.requestAnimationFrame(tick);
    };
    const started = () => {
      if (introDone || video.currentTime >= HERO_CUT.logoEnd) return finishIntro();
      // Never leave the page without its words if the film stalls on the title.
      safety = window.setTimeout(finishIntro, 3000);
    };
    video.addEventListener('playing', started, {once: true});
    video.addEventListener('timeupdate', follow);
    video.addEventListener('ended', follow);
    void video.play()?.catch(() => {});
    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(safety);
      video.removeEventListener('playing', started);
      video.removeEventListener('timeupdate', follow);
      video.removeEventListener('ended', follow);
      video.pause();
      setIntro(false);
    };
  }, [src, setIntro, onChapter]);
  return <video ref={ref} src={src} poster={poster} muted playsInline preload="auto" aria-hidden="true" />;
}

export function Showcase(p: ShowcaseProps) {
  // The page opens dark on the logo; words and story arrive when the title is done.
  const [intro, setIntro] = useState(() => !p.publicPreview && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const timer = window.setTimeout(() => setIntro(false), 5000);
    return () => window.clearTimeout(timer);
  }, []);
  const [films, setFilms] = useState<ShowcaseFilm[]>([]);
  const [message, setMessage] = useState('Opening the Caprica film collection...');
  const [hero, setHero] = useState(false);
  const [chapter, setChapter] = useState(0);
  const now = HERO_CHAPTERS[chapter] ?? HERO_CHAPTERS[0];
  const [filter, setFilter] = useState('all');
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 40);
    update();
    window.addEventListener('scroll', update, {passive: true});
    return () => window.removeEventListener('scroll', update);
  }, []);
  const [reduced, setReduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const change = () => setReduced(!!query?.matches);
    query?.addEventListener?.('change', change);
    return () => query?.removeEventListener?.('change', change);
  }, []);

  useEffect(() => {
    let active = true;
    if (!p.publicPreview) {
      p.loadFilms()
        .then(items => { if (active) { setFilms(items); setMessage('Archive films are available inside the Studio'); } })
        .catch(() => { if (active) setMessage('Archive films are available inside the Studio'); });
    }
    // The showreel is public: it ships with the website, so the public site plays it without
    // asking any server. Inside the studio it is checked first. The archive stays private.
    if (reduced) setIntro(false);
    else if (p.publicPreview) setHero(true);
    else {
      p.probeHero()
        .then(ok => { if (active) { setHero(ok); if (!ok) setIntro(false); } })
        .catch(() => { if (active) setIntro(false); });
    }
    return () => { active = false; };
  }, [p.publicPreview, reduced]);

  const titles = useMemo(() => [...new Set(films.map(film => film.title))], [films]);
  const shown = filter === 'all' ? films : films.filter(film => film.title === filter);
  const posters = useMemo(() => {
    const seen = new Set<string>();
    return films.filter(film => film.poster && !seen.has(film.id) && seen.add(film.id));
  }, [films]);
  const scroll = (id: string) => document.getElementById(id)?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth'});
  const create = (className = 'cl-primary') => (
    <button className={className} disabled={p.publicPreview} onClick={() => p.enter(true)}>
      {p.publicPreview ? 'Studio access coming soon' : 'Begin a production'}
    </button>
  );
  const logo = (
    <span className="caprica-lockup">
      <span className="caprica-symbol" aria-hidden="true"><img src="/static/caprica-mark.svg" alt="" /></span>
      <span className="caprica-wordmark"><strong>CAPRICA</strong><small>STUDIO</small></span>
    </span>
  );

  return <div className="cl">
    <nav className={`cl-nav ${scrolled ? 'is-solid' : ''}`} aria-label="Landing navigation">
      <div className="cl-brand">{logo}</div>
      <div className="cl-nav-links">
        <button onClick={() => scroll('landingShowcase')}>The work</button>
        <button onClick={() => scroll('landingProcess')}>Our method</button>
        <button onClick={() => scroll('landingPrinciples')}>Why Caprica</button>
      </div>
      <button className="cl-nav-cta" disabled={p.publicPreview} onClick={() => p.enter(false)}>
        {p.publicPreview ? 'Studio access coming soon' : 'Enter Studio'}
      </button>
    </nav>

    <header className={`cl-hero ${intro ? 'is-intro' : ''}`}>
      <div className="cl-hero-media" id="landingHeroMedia" aria-hidden="true">
        <img src={HERO_POSTER} alt="" />
        {hero && !reduced && <HeroFilm src={p.heroUrl} poster={HERO_POSTER} setIntro={setIntro} onChapter={setChapter} />}
      </div>
      <div className="cl-hero-copy">
        <p className="cl-kicker">Independent AI film direction</p>
        <h1><span>A film studio</span><em>that listens</em><span>before it directs.</span></h1>
        <p className="cl-lead">Caprica turns a song and an idea into a production you can understand before a single credit is spent. Story, cast, continuity, motion and final cut — directed as one film.</p>
        <div className="cl-actions">
          {create()}
          <button className="cl-watch" onClick={() => scroll('landingShowcase')}>
            <span aria-hidden="true">▶</span> View selected work
          </button>
        </div>
        <ul className="cl-proof">
          <li>Story before prompts</li>
          <li>Human approval</li>
          <li>Credits visible upfront</li>
        </ul>
      </div>
      <div className="cl-hero-caption" aria-live="off">
        <small>Now playing · The Caprica showreel</small>
        <b key={now.label} className="cl-hero-chapter">{hero ? now.label : 'Eight films, one studio'}</b>
        <span>{hero ? now.copy : 'Colour, purity, lyric video, lip-sync, human & AI, science fiction, dance and action — all made in Caprica.'}</span>
        {hero && <span className="cl-hero-dots" aria-hidden="true">{HERO_CHAPTERS.map((item, index) => <i key={item.label} className={index === chapter ? 'is-on' : ''} />)}</span>}
      </div>
      <div className="cl-seal" aria-hidden="true">
        <svg viewBox="0 0 120 120">
          <defs><path id="cl-seal-ring" d="M60 60 m-46 0 a46 46 0 1 1 92 0 a46 46 0 1 1 -92 0" /></defs>
          <text><textPath href="#cl-seal-ring" textLength="286" lengthAdjust="spacing">SOUND · STORY · SCREEN · </textPath></text>
        </svg>
        <img src="/static/caprica-mark.svg" alt="" />
      </div>
      <span className="cl-scroll" aria-hidden="true">Scroll to explore</span>
    </header>

    <section className="cl-section cl-work" id="landingShowcase">
      <div className="cl-head">
        <div><p className="cl-kicker">01 · Selected work</p><h2>Films made as worlds, not prompts.</h2></div>
        <p>Scenes from the Caprica archive. Different stories, characters and visual languages — each held together by a reviewable production document and one continuous direction.</p>
      </div>
      {titles.length > 1 && <div className="cl-filters" role="tablist" aria-label="Filter the work">
        {['all', ...titles].map(title => <button key={title} role="tab" aria-selected={filter === title} onClick={() => setFilter(title)}>{title === 'all' ? 'All' : title}</button>)}
      </div>}
      <div className="cl-gallery" id="landingShowcaseGrid">
        {p.publicPreview
          ? <div className="cl-loading">The production workspace and film archive are private. Public access is not yet enabled.</div>
          : shown.length
            ? shown.map((f, i) => <button key={`${f.id}:${f.motion}`} className={`cl-tile ${TILE_RHYTHM[i % TILE_RHYTHM.length] ? `is-${TILE_RHYTHM[i % TILE_RHYTHM.length]}` : ''}`} aria-label={`Open ${f.title} in Caprica Studio`} onClick={() => p.open(f.id)}>
                <Video src={f.motion} poster={f.poster} mime={f.mime} reduced={reduced} />
                <span className="cl-tile-copy"><small>{f.label}</small><b>{f.title}</b></span>
                <i aria-hidden="true">↗</i>
              </button>)
            : <div className="cl-loading">{message}</div>}
      </div>
    </section>

    <section className="cl-statement">
      <p className="cl-kicker">02 · What makes Caprica different</p>
      <blockquote>Most tools start with generation. Caprica starts with <em>understanding what the film must mean.</em></blockquote>
      <div className="cl-statement-note">
        <p>Caprica AI interviews you lightly, fills a structured production form and shows where every important decision came from. You approve the document — not an uncheckable chat transcript.</p>
        <p>After approval, the route is controlled: who appears, what they wear, where they are, when a motif returns, how much lip-sync or dance the film needs, and what those choices cost.</p>
      </div>
    </section>

    <section className="cl-method" id="landingProcess">
      <div className="cl-head cl-section">
        <div><p className="cl-kicker">03 · The Caprica method</p><h2>One clear route from sound to screen.</h2></div>
        <p>Simple for the maker. Precise underneath. No generation starts before the current production and credit estimate are clear.</p>
      </div>
      <ol className="cl-chapters">
        {METHOD.map(([name, title, copy], i) => {
          const backdrop = posters[i % Math.max(1, posters.length)]?.poster;
          return <li className="cl-chapter" key={name}>
            {backdrop && <img src={backdrop} alt="" loading="lazy" />}
            <span className="cl-chapter-dot" aria-hidden="true" />
            <div className="cl-chapter-copy">
              <small>Chapter {pad(i + 1)} · {name}</small>
              <h3>{title}</h3>
              <p>{copy}</p>
            </div>
            <span className="cl-chapter-count" aria-hidden="true"><b>{pad(i + 1)}</b><i /><span>{pad(METHOD.length)}</span></span>
          </li>;
        })}
      </ol>
    </section>

    <section className="cl-section cl-principles" id="landingPrinciples">
      <div className="cl-head">
        <div><p className="cl-kicker">04 · Designed for confidence</p><h2>Power should feel calm.</h2></div>
        <p>The interface keeps the difficult production logic close, then stays quiet when everything is right.</p>
      </div>
      <div className="cl-principle-grid">
        {PRINCIPLES.map(([name, title, copy], i) => <article key={name}>
          <span>{pad(i + 1)}</span><small>{name}</small><h3>{title}</h3><p>{copy}</p>
        </article>)}
      </div>
    </section>

    <section className="cl-final">
      {posters[0]?.poster && <img src={posters[posters.length - 1]!.poster} alt="" loading="lazy" aria-hidden="true" />}
      <div>
        <p className="cl-kicker">05 · The next frame</p>
        <h2>Give your idea a film language.</h2>
        {create()}
      </div>
    </section>

    <footer className="cl-footer">
      <span>Independent film intelligence · 2026</span>
      {logo}
      <span>Your archive remains inside your workspace</span>
    </footer>
  </div>;
}
